import React, { createContext, useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { authService } from '../services/authService';
import { syncSignedInHint } from '../utils/signedInHint';
import { t } from '../i18n';
import apiClient from '../services/apiClient';
import { setDisplayCurrency, loadDisplayRates } from '../utils/money';

// Token storage keys
const ACCESS_TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_INFO_KEY = 'user_info';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [accessToken, setAccessToken] = useState(localStorage.getItem(ACCESS_TOKEN_KEY));
    const [refreshToken, setRefreshToken] = useState(localStorage.getItem(REFRESH_TOKEN_KEY));
    // 价格按用户偏好货币折算展示（shared/utils/money.js）；汇率到了之后 fxTick 变化让使用 AuthContext 的页面重渲染
    const [fxTick, setFxTick] = useState(0);
    setDisplayCurrency(currentUser?.currency);
    useEffect(() => {
        loadDisplayRates(() => apiClient.get('/currencies')).then(() => setFxTick((n) => n + 1));
    }, []);

    // 更新令牌
    const updateTokens = useCallback((tokens) => {
        if (tokens?.accessToken) {
            localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
            syncSignedInHint();
            setAccessToken(tokens.accessToken);
        }

        if (tokens?.refreshToken) {
            localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
            setRefreshToken(tokens.refreshToken);
        }
    }, []);

    // 清除令牌
    const clearTokens = useCallback(() => {
        localStorage.removeItem(ACCESS_TOKEN_KEY);
            syncSignedInHint();
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(USER_INFO_KEY);
        setAccessToken(null);
        setRefreshToken(null);
        setCurrentUser(null);
    }, []);

    // 刷新访问令牌
    const refreshAccessToken = useCallback(async () => {
        if (!refreshToken) return null;

        try {
            const result = await authService.refreshToken(refreshToken);
            if (result.success) {
                updateTokens(result.data);
                return result.data.accessToken;
            } else {
                clearTokens();
                return null;
            }
        } catch (error) {
            console.error('Token refresh failed:', error);
            clearTokens();
            return null;
        }
    }, [refreshToken, updateTokens, clearTokens]);

    // 登录
    const login = async (username, password, cfkey, remember = false) => {
        try {
            const result = await authService.login(username, password, cfkey, remember);
            if (result.success) {
                // 需要两步验证：不设置用户/令牌，把挑战令牌交给登录页进入验证码步骤
                if (result.requires2fa) {
                    return {
                        success: true,
                        requires2fa: true,
                        challengeToken: result.challengeToken
                    };
                }
                updateTokens(result.data);
                setCurrentUser(result.data.user);
                return { success: true };
            } else {
                message.error(result.error || result.message || t('Sign-in failed'));
                return {
                    success: false,
                    message: result.error || result.message,
                    // 邮箱未验证的 pending 账号：登录页据此展示"重发验证邮件"入口
                    resendable: result.resendable,
                    email: result.email
                };
            }
        } catch (error) {
            message.error(t('Sign-in failed: {{value}}', { value: error.message || t('Unknown error') }));
            return { success: false, message: error.message };
        }
    };

    // 完成两步验证登录（code 可为 6 位验证码或备用码）
    const verify2fa = async (challengeToken, code, remember = false) => {
        try {
            const result = await authService.verify2fa(challengeToken, code, remember);
            if (result.success) {
                updateTokens(result.data);
                setCurrentUser(result.data.user);
                return { success: true };
            } else {
                message.error(result.message || t('Two-step verification failed'));
                return { success: false, message: result.message };
            }
        } catch (error) {
            message.error(t('Two-step verification failed: {{value}}', { value: error.message || t('Unknown error') }));
            return { success: false, message: error.message };
        }
    };

    // Google / GitHub 登录回调的一次性 code 换令牌（可能要求 2FA）
    const oauthExchange = async (code) => {
        const result = await authService.oauthExchange(code);
        if (result.success && !result.requires2fa) {
            updateTokens(result.data);
            setCurrentUser(result.data.user);
            return { success: true };
        }
        return result;
    };

    // 注册
    const register = async (userData) => {
        try {
            const result = await authService.register(userData);
            if (result.success) {
                // 使用后端返回的真实消息（如"验证邮件已发送"/"请等待管理员审核"），
                // 避免误导用户立刻去登录而遇到 pending 账号登录失败。
                message.success(result.message || t('Registration successful'));
                return {
                    success: true,
                    message: result.message,
                    verificationSent: !!result.verification_sent
                };
            } else {
                message.error(result.message || t('Registration failed'));
                return { success: false, message: result.error || result.message };
            }
        } catch (error) {
            message.error(t('Registration failed: {{value}}', { value: error.message || t('Unknown error') }));
            return { success: false, message: error.message };
        }
    };

    // 重置密码
    const resetPassword = async (account) => {
        try {
            const result = await authService.resetPassword(account);
            if (result.success) {
                message.success(t("If the account exists, we've sent a reset link to its email"));
                return { success: true };
            } else {
                message.error(result.message || t('Failed to reset password'));
                return { success: false, message: result.message };
            }
        } catch (error) {
            message.error(t('Failed to reset password: {{value}}', { value: error.message || t('Unknown error') }));
            return { success: false, message: error.message };
        }
    };

    // 确认重置密码（携带邮件里的 token 设置新密码）
    // ResetPassword 页面通过 useAuth() 调用本方法；此前 context 未提供，
    // 导致确认重置时抛出 "confirmResetPassword is not a function"。
    const confirmResetPassword = async (token, password) => {
        try {
            const result = await authService.confirmResetPassword(token, password);
            if (result.success) {
                message.success(t('Password reset. Sign in with your new password'));
                return { success: true };
            } else {
                message.error(result.message || result.error || t('Failed to confirm password reset'));
                return { success: false, message: result.message || result.error };
            }
        } catch (error) {
            message.error(t('Failed to confirm password reset: {{value}}', { value: error.message || t('Unknown error') }));
            return { success: false, message: error.message };
        }
    };

    // 更新用户信息
    const updateUser = useCallback((userData) => {
        setCurrentUser(prev => ({
            ...prev,
            ...userData
        }));
        // 同时更新本地存储
        const storedUser = localStorage.getItem(USER_INFO_KEY);
        if (storedUser) {
            try {
                const parsed = JSON.parse(storedUser);
                localStorage.setItem(USER_INFO_KEY, JSON.stringify({ ...parsed, ...userData }));
            } catch (e) {
                // ignore
            }
        }
    }, []);

    // 登出
    // Voluntary logout (user clicked logout button) — go to bare /login without
    // a return_to (we don't want to bring them back to where they were).
    const logout = useCallback(async () => {
        try {
            await authService.logout();
        } finally {
            clearTokens();
            setCurrentUser(null);
            window.location.href = '/login';
        }
    }, [clearTokens]);

    // 监听登出事件 (dispatched by apiClient on 401)
    // This IS an involuntary logout, so we want return_to so the user lands
    // back where they were after re-auth. apiClient already redirects with
    // ?return_to=, but if a future caller dispatches auth:logout without
    // redirecting, we still need to handle it here.
    useEffect(() => {
        const handleLogoutEvent = () => {
            clearTokens();
            setCurrentUser(null);
            // If apiClient already initiated a redirect (the common case),
            // don't double-navigate. Detect via the isLoggingOut flag indirectly:
            // we check if the URL is about to change to /login.
            const { pathname } = window.location;
            const onAuthPage = /^\/(login|register|reset-password)(\/|$)/.test(pathname);
            if (onAuthPage) return;
            const here = pathname + window.location.search + window.location.hash;
            window.location.href = '/login?return_to=' + encodeURIComponent(here);
        };

        window.addEventListener('auth:logout', handleLogoutEvent);

        return () => {
            window.removeEventListener('auth:logout', handleLogoutEvent);
        };
    }, [clearTokens]);

    // 初始化时检查登录状态
    useEffect(() => {
        const checkAuthStatus = async () => {
            setLoading(true);

            if (accessToken) {
                try {
                    // 尝试获取当前用户信息
                    const result = await authService.getCurrentUser();
                    if (result.success) {
                        setCurrentUser(result.data);
                    } else {
                        // getCurrentUser 失败有两种形态，都表示当前 access token 不可用：
                        //   1) HTTP 401（token 过期/失效，会走 getCurrentUser 的 catch 返回 status:401）
                        //   2) HTTP 200 但 body {authenticated:false}（后端 /auth/check 对未认证恒返 200，
                        //      返回 status:200 + success:false）
                        // 旧逻辑只在 status===401 时尝试刷新，形态 2 直接 clearTokens →
                        // 持有有效 refresh_token 的用户在 access 过期后刷新页面会被误登出。
                        // 现统一：只要还有 refresh_token 就先尝试续期，失败再登出。
                        const newToken = await refreshAccessToken();
                        if (newToken) {
                            // 刷新成功，再次尝试获取用户信息
                            const retryResult = await authService.getCurrentUser();
                            if (retryResult.success) {
                                setCurrentUser(retryResult.data);
                            } else {
                                clearTokens();
                            }
                        } else {
                            // 无 refresh_token 或刷新失败（refreshAccessToken 内部已在失败时清理）
                            clearTokens();
                        }
                    }
                } catch (error) {
                    console.error('Auth check error:', error);
                    clearTokens();
                }
            } else {
                // 尝试从本地存储加载用户信息（作为后备）
                const storedUser = localStorage.getItem(USER_INFO_KEY);
                if (storedUser) {
                    try {
                        setCurrentUser(JSON.parse(storedUser));
                    } catch (e) {
                        // 无效的JSON，清除存储
                        localStorage.removeItem(USER_INFO_KEY);
                    }
                }
            }

            setLoading(false);
        };

        checkAuthStatus();
    }, [accessToken, refreshAccessToken, clearTokens]);

    const value = {
        currentUser,
        fxTick,
        accessToken,
        refreshToken,
        loading,
        login,
        verify2fa,
        oauthExchange,
        register,
        logout,
        resetPassword,
        confirmResetPassword,
        refreshAccessToken,
        updateTokens,
        clearTokens,
        updateUser
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
